import React from "react";
import { Box, TextField, Typography } from "@mui/material";
import { RiStackLine } from "react-icons/ri";
import {
  renderCurrencyIcon,
  renderCurrencyIconColored,
} from "../../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import { formatAmountWithCommas, isoDay } from "./helpers";

// Floating-label wrapper. Module-scope so wrapped inputs keep focus
// across parent re-renders. The label slot is inline-flex/center so a
// currency icon embedded in the label sits on the same baseline as
// the text.
const PaymentFieldShell = ({ label, children }) => (
  <Box sx={{ width: "100%", position: "relative" }}>
    <Box
      sx={{
        position: "absolute",
        top: -8,
        left: 12,
        background: "#fff",
        padding: "0 6px",
        fontSize: 12,
        color: "#6B7280",
        fontFamily: "Inter Tight",
        zIndex: 1,
        pointerEvents: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        lineHeight: 1,
      }}
    >
      {label}
    </Box>
    {children}
  </Box>
);

const PAYMENTS_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    fontFamily: "Inter Tight",
    fontSize: 14,
    "& fieldset": { borderColor: "#D7D8D9", borderWidth: "1px" },
    "&:hover fieldset": { borderColor: "#9CA3AF", borderWidth: "1px" },
    "&.Mui-focused fieldset": { borderColor: "#4D6BF6", borderWidth: "1px" },
    "&.Mui-focused": { boxShadow: "none", outline: "none" },
  },
  "& .MuiOutlinedInput-input": { padding: "12px 12px" },
};

const inputSx = (errored) => ({
  width: "100%",
  border: `1px solid ${errored ? "#DC2626" : "#D7D8D9"}`,
  borderRadius: "10px",
  padding: "12px 12px",
  fontSize: 14,
  fontFamily: "Inter Tight",
  color: "#0D0D0E",
  background: "#fff",
  outline: "none",
  boxSizing: "border-box",
  textAlign: "left",
  "&::placeholder": { color: "#9CA3AF" },
  "&:focus": { borderColor: errored ? "#DC2626" : "#4D6BF6" },
});

// Readonly inputs share the same white background as the editable
// inputs — the only signal that they're not editable is the
// `not-allowed` cursor.
const readonlyAmountSx = {
  width: "100%",
  border: "1px solid #D7D8D9",
  borderRadius: "10px",
  padding: "12px 12px",
  fontSize: 14,
  fontFamily: "Inter Tight",
  color: "#0D0D0E",
  background: "#fff",
  outline: "none",
  boxSizing: "border-box",
  textAlign: "left",
  cursor: "not-allowed",
};

const fieldErrorSx = (errored) =>
  errored
    ? {
        "& .MuiOutlinedInput-root": {
          "& fieldset": { borderColor: "#DC2626", borderWidth: "1px" },
          "&:hover fieldset": {
            borderColor: "#DC2626",
            borderWidth: "1px",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#DC2626",
            borderWidth: "1px",
          },
        },
      }
    : {};

// Payments tab — fixed to the SPLIT strategy. Two payment lines: a
// "Downpayment" and a "Final Payment". User can edit each line's
// payment name, percentage, and due date; amount is derived from
// percentage × CR price and shown readonly.
export default function PaymentsTab({
  crPrice,
  lines,
  setLines,
  readOnly,
  errors,
}) {
  const price = Number(crPrice || 0);

  const updateLine = (idx, patch) =>
    setLines((prev) =>
      prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    );

  // When the user changes one line's percentage, the other line's
  // percentage auto-balances to (100 − new%). Amounts on both lines
  // recompute from the resulting percentages.
  const handlePercentageChange = (idx, raw) => {
    if (readOnly) return;
    const sanitized = raw.replace(/[^\d.]/g, "");
    if (sanitized === "") {
      setLines((prev) =>
        prev.map((l, i) =>
          i === idx ? { ...l, percentage: "" } : l,
        ),
      );
      return;
    }
    let n = Number(sanitized);
    if (!Number.isFinite(n)) return;
    if (n < 0) n = 0;
    if (n > 100) n = 100;
    setLines((prev) => {
      const next = [...prev];
      const other = idx === 0 ? 1 : 0;
      next[idx] = { ...(next[idx] || {}), percentage: String(n) };
      next[other] = {
        ...(next[other] || {}),
        percentage: String(Number((100 - n).toFixed(2))),
      };
      return next;
    });
  };

  const hasFieldError = (idx, field) => !!errors?.rows?.[idx]?.[field];

  // Resolve each line's computed amount on the fly so the readonly
  // display always reflects the latest percentage + CR price.
  const amountFor = (line) => {
    const pct = Number(line?.percentage || 0);
    if (!Number.isFinite(pct)) return 0;
    return (pct / 100) * price;
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          width: { xs: "100%", md: "75%" },
          background: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {/* Header — title on the left, SPLIT pill flush right. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            padding: "18px 20px",
          }}
        >
          <RiStackLine style={{ fontSize: 24, color: "#4D6BF6" }} />
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              color: "#0D0D0E",
              fontFamily: "Inter Tight",
            }}
          >
            Payment Services
          </Typography>
          <Box
            component="span"
            sx={{
              ml: "auto",
              display: "inline-flex",
              alignItems: "center",
              padding: "3px 10px",
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 999,
              backgroundColor: "#EFF6FF",
              color: "#1D4ED8",
              fontFamily: "Inter Tight",
              whiteSpace: "nowrap",
              letterSpacing: "0.2px",
            }}
          >
            SPLIT
          </Box>
        </Box>

        {/* Body */}
        <Box
          sx={{
            padding: "10px 20px 22px",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {/* Read-only CR price */}
          <PaymentFieldShell
            label={
              <>
                Change Request Price (
                {renderCurrencyIconColored("black-xs-small")})
              </>
            }
          >
            <Box
              component="input"
              type="text"
              readOnly
              placeholder="Enter price"
              value={price ? formatAmountWithCommas(String(price)) : ""}
              sx={readonlyAmountSx}
            />
          </PaymentFieldShell>

          {(lines || []).map((line, idx) => (
            <Box
              key={idx}
              sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#0D0D0E",
                  fontFamily: "Inter Tight",
                  letterSpacing: "0.2px",
                }}
              >
                {idx === 0 ? "1st Payment" : "2nd Payment"}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  // On mobile use a 2-column grid so Amount + Percentage
                  // sit side-by-side; Payment Name and Due Date span the
                  // full row above and below them.
                  gridTemplateColumns: {
                    xs: "1fr 1fr",
                    md: "1.4fr 1fr 0.8fr 1fr",
                  },
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    gridColumn: { xs: "1 / -1", md: "auto" },
                  }}
                >
                  <PaymentFieldShell label="Payment Name">
                    <Box
                      component="input"
                      type="text"
                      placeholder="Enter payment name"
                      value={line.displayName ?? ""}
                      readOnly={readOnly}
                      onChange={(e) =>
                        updateLine(idx, { displayName: e.target.value })
                      }
                      sx={inputSx(hasFieldError(idx, "displayName"))}
                    />
                  </PaymentFieldShell>
                </Box>

                <Box
                  sx={{
                    gridColumn: { xs: "1 / 2", md: "auto" },
                  }}
                >
                  <PaymentFieldShell
                    label={
                      <>
                        Amount ({renderCurrencyIconColored("black-xs-small")})
                      </>
                    }
                  >
                    <Box
                      component="input"
                      type="text"
                      readOnly
                      value={formatAmountWithCommas(
                        String(amountFor(line).toFixed(2)),
                      )}
                      sx={readonlyAmountSx}
                    />
                  </PaymentFieldShell>
                </Box>

                <Box
                  sx={{
                    gridColumn: { xs: "2 / 3", md: "auto" },
                  }}
                >
                  <PaymentFieldShell label="Percentage (%)">
                    <Box
                      component="input"
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      value={line.percentage ?? ""}
                      readOnly={readOnly}
                      onChange={(e) =>
                        handlePercentageChange(idx, e.target.value)
                      }
                      sx={inputSx(hasFieldError(idx, "percentage"))}
                    />
                  </PaymentFieldShell>
                </Box>

                <Box
                  sx={{
                    gridColumn: { xs: "1 / -1", md: "auto" },
                  }}
                >
                  <PaymentFieldShell label="Due Date">
                    <TextField
                      type="date"
                      fullWidth
                      value={isoDay(line.dueDate)}
                      onChange={(e) =>
                        updateLine(idx, { dueDate: e.target.value })
                      }
                      inputProps={{ readOnly }}
                      placeholder="Select due date"
                      sx={[
                        PAYMENTS_FIELD_SX,
                        fieldErrorSx(hasFieldError(idx, "dueDate")),
                      ]}
                    />
                  </PaymentFieldShell>
                </Box>
              </Box>
            </Box>
          ))}

          {/* Footer line: running total readout so the user can see the
              two amounts add up to the CR price. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
              fontSize: 13,
              color: "#6B7280",
              fontFamily: "Inter Tight",
            }}
          >
            Total:
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                color: "#0D0D0E",
                fontWeight: 600,
                fontSize: 15,
              }}
            >
              {renderCurrencyIcon("black-xs-small")}
              {formattedAmountWithoutAed(
                (lines || []).reduce((sum, l) => sum + amountFor(l), 0),
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
